import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import {
  FiCamera,
  FiGrid,
  FiMaximize,
  FiMinimize,
  FiRefreshCw,
  FiRotateCw,
  FiX,
} from "react-icons/fi";

interface StlViewerProps {
  fileName: string;
  arrayBuffer: ArrayBuffer | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
}

type ViewPreset = "iso" | "front" | "back" | "left" | "right" | "top" | "bottom";
type DisplayMode = "solid" | "wireframe" | "solid-edges";

interface ModelStats {
  triangles: number;
  vertices: number;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
}

interface ViewerSettings {
  displayMode: DisplayMode;
  color: string;
  opacity: number;
  smoothShading: boolean;
  showGrid: boolean;
  showAxes: boolean;
  autoRotate: boolean;
  orthographic: boolean;
  darkBackground: boolean;
}

interface ViewerApi {
  applySettings: (settings: ViewerSettings) => void;
  fitToView: () => void;
  setViewPreset: (preset: ViewPreset) => void;
  takeScreenshot: (name: string) => void;
  resize: () => void;
  dispose: () => void;
}

const MODEL_COLORS = [
  "#9ca3af",
  "#60a5fa",
  "#34d399",
  "#fbbf24",
  "#f87171",
  "#c084fc",
  "#f8fafc",
  "#1f2937",
];

const PRESETS: ViewPreset[] = [
  "iso",
  "front",
  "back",
  "left",
  "right",
  "top",
  "bottom",
];

const formatMm = (n: number) => `${n.toFixed(2)} mm`;

const ToolbarBtn = ({
  active,
  onClick,
  title,
  children,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: ReactNode;
  disabled?: boolean;
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={`rounded px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
      active
        ? "bg-[#1DA8C6] text-white"
        : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

const StlViewer = ({
  fileName,
  arrayBuffer,
  loading = false,
  error = null,
  onClose,
}: StlViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<ViewerApi | null>(null);

  const [renderError, setRenderError] = useState<string | null>(null);
  const [stats, setStats] = useState<ModelStats | null>(null);
  const [ready, setReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [displayMode, setDisplayMode] = useState<DisplayMode>("solid");
  const [modelColor, setModelColor] = useState(MODEL_COLORS[0]);
  const [opacity, setOpacity] = useState(1);
  const [smoothShading, setSmoothShading] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(true);
  const [autoRotate, setAutoRotate] = useState(false);
  const [orthographic, setOrthographic] = useState(false);
  const [darkBackground, setDarkBackground] = useState(false);
  const [activePreset, setActivePreset] = useState<ViewPreset>("iso");

  useEffect(() => {
    if (!arrayBuffer || !containerRef.current) return;

    setRenderError(null);
    setReady(false);
    setStats(null);

    const container = containerRef.current;
    let disposed = false;
    let frameId = 0;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f4f6);

    const getSize = () => ({
      width: Math.max(container.clientWidth, 1),
      height: Math.max(container.clientHeight, 1),
    });

    const { width, height } = getSize();

    const perspectiveCamera = new THREE.PerspectiveCamera(
      45,
      width / height,
      0.01,
      10000
    );
    const orthographicCamera = new THREE.OrthographicCamera(
      -1,
      1,
      1,
      -1,
      0.01,
      10000
    );
    let activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera =
      perspectiveCamera;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    const key = new THREE.DirectionalLight(0xffffff, 1.05);
    key.position.set(1.5, 2.2, 1.2);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-1.8, 0.6, -1.2);
    const rim = new THREE.DirectionalLight(0xffffff, 0.25);
    rim.position.set(0.2, -1.5, 1.8);
    scene.add(ambient, key, fill, rim);

    const controls = new OrbitControls(activeCamera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = true;
    controls.minDistance = 0.1;
    controls.maxDistance = 5000;
    controls.autoRotateSpeed = 1.6;

    let mesh: THREE.Mesh | null = null;
    let edges: THREE.LineSegments | null = null;
    let gridHelper: THREE.GridHelper | null = null;
    let axesHelper: THREE.AxesHelper | null = null;
    let modelRadius = 50;
    let modelSize = new THREE.Vector3(1, 1, 1);
    let lastDarkBackground = false;
    let lastSmoothShading = true;

    const syncOrthoFrustum = () => {
      const size = getSize();
      const aspect = size.width / size.height;
      const viewSize = Math.max(modelRadius * 2.2, 1);
      orthographicCamera.left = (-viewSize * aspect) / 2;
      orthographicCamera.right = (viewSize * aspect) / 2;
      orthographicCamera.top = viewSize / 2;
      orthographicCamera.bottom = -viewSize / 2;
      orthographicCamera.updateProjectionMatrix();
    };

    const fitCamera = (
      camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
      direction: THREE.Vector3
    ) => {
      const dir = direction.clone().normalize();
      const distance =
        camera instanceof THREE.PerspectiveCamera
          ? Math.max(
              modelRadius / Math.sin(THREE.MathUtils.degToRad(camera.fov / 2)),
              1
            ) * 1.15
          : Math.max(modelRadius * 2.4, 1);

      camera.position.copy(dir.multiplyScalar(distance));
      camera.up.set(0, 1, 0);
      if (Math.abs(dir.y) > 0.95) {
        camera.up.set(0, 0, dir.y > 0 ? -1 : 1);
      }
      camera.lookAt(0, 0, 0);
      controls.target.set(0, 0, 0);
      controls.update();
      if (camera instanceof THREE.OrthographicCamera) syncOrthoFrustum();
    };

    const rebuildGrid = (dark: boolean, visible: boolean) => {
      if (gridHelper) {
        scene.remove(gridHelper);
        gridHelper.dispose();
      }
      const gridSize = Math.max(modelRadius * 4, 20);
      gridHelper = new THREE.GridHelper(
        gridSize,
        20,
        dark ? 0x64748b : 0x94a3b8,
        dark ? 0x334155 : 0xcbd5e1
      );
      gridHelper.position.y = -modelSize.y / 2;
      gridHelper.visible = visible;
      scene.add(gridHelper);
    };

    const applySettings = (settings: ViewerSettings) => {
      if (!mesh) return;

      const material = mesh.material as THREE.MeshStandardMaterial;
      material.color.set(settings.color);
      material.wireframe = settings.displayMode === "wireframe";
      material.transparent = settings.opacity < 1;
      material.opacity = settings.opacity;
      material.flatShading = !settings.smoothShading;
      material.side = THREE.DoubleSide;
      material.needsUpdate = true;
      if (settings.smoothShading !== lastSmoothShading) {
        lastSmoothShading = settings.smoothShading;
        mesh.geometry.computeVertexNormals();
      }

      if (edges) edges.visible = settings.displayMode === "solid-edges";
      if (axesHelper) axesHelper.visible = settings.showAxes;

      controls.autoRotate = settings.autoRotate;
      scene.background = new THREE.Color(
        settings.darkBackground ? 0x111827 : 0xf3f4f6
      );

      if (settings.darkBackground !== lastDarkBackground) {
        lastDarkBackground = settings.darkBackground;
        rebuildGrid(settings.darkBackground, settings.showGrid);
      } else if (gridHelper) {
        gridHelper.visible = settings.showGrid;
      }

      const wantOrtho = settings.orthographic;
      const isOrtho = activeCamera instanceof THREE.OrthographicCamera;
      if (wantOrtho !== isOrtho) {
        const prevPos = activeCamera.position.clone();
        const prevTarget = controls.target.clone();
        activeCamera = wantOrtho ? orthographicCamera : perspectiveCamera;
        activeCamera.position.copy(prevPos);
        if (wantOrtho) {
          syncOrthoFrustum();
        } else {
          const size = getSize();
          perspectiveCamera.aspect = size.width / size.height;
          perspectiveCamera.updateProjectionMatrix();
        }
        controls.object = activeCamera;
        controls.target.copy(prevTarget);
        controls.update();
      }
    };

    try {
      const geometry = new STLLoader().parse(arrayBuffer);
      geometry.computeVertexNormals();
      geometry.center();
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      const box = geometry.boundingBox!;
      modelSize = new THREE.Vector3();
      box.getSize(modelSize);
      modelRadius =
        geometry.boundingSphere?.radius || modelSize.length() / 2 || 50;

      const material = new THREE.MeshStandardMaterial({
        color: MODEL_COLORS[0],
        metalness: 0.12,
        roughness: 0.48,
        side: THREE.DoubleSide,
      });
      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry, 25),
        new THREE.LineBasicMaterial({
          color: 0x111827,
          transparent: true,
          opacity: 0.55,
        })
      );
      edges.visible = false;
      scene.add(edges);

      rebuildGrid(false, true);

      axesHelper = new THREE.AxesHelper(Math.max(modelRadius * 0.75, 5));
      axesHelper.position.y = -modelSize.y / 2;
      scene.add(axesHelper);

      const triangleCount = geometry.index
        ? geometry.index.count / 3
        : geometry.attributes.position.count / 3;

      setStats({
        triangles: Math.round(triangleCount),
        vertices: geometry.attributes.position.count,
        sizeX: modelSize.x,
        sizeY: modelSize.y,
        sizeZ: modelSize.z,
      });

      syncOrthoFrustum();
      fitCamera(activeCamera, new THREE.Vector3(1, 0.85, 1));
      setReady(true);
    } catch (err) {
      console.error(err);
      setRenderError("Could not parse this STL file.");
    }

    const animate = () => {
      if (disposed) return;
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, activeCamera);
    };
    animate();

    const api: ViewerApi = {
      applySettings,
      fitToView: () => fitCamera(activeCamera, activeCamera.position.clone()),
      setViewPreset: (preset) => {
        const map: Record<ViewPreset, THREE.Vector3> = {
          iso: new THREE.Vector3(1, 0.85, 1),
          front: new THREE.Vector3(0, 0, 1),
          back: new THREE.Vector3(0, 0, -1),
          left: new THREE.Vector3(-1, 0, 0),
          right: new THREE.Vector3(1, 0, 0),
          top: new THREE.Vector3(0, 1, 0.0001),
          bottom: new THREE.Vector3(0, -1, 0.0001),
        };
        fitCamera(activeCamera, map[preset]);
      },
      takeScreenshot: (name) => {
        const dataUrl = renderer.domElement.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `${name.replace(/\.stl$/i, "") || "model"}-preview.png`;
        a.click();
      },
      resize: () => {
        const size = getSize();
        if (activeCamera instanceof THREE.PerspectiveCamera) {
          activeCamera.aspect = size.width / size.height;
          activeCamera.updateProjectionMatrix();
        } else {
          syncOrthoFrustum();
        }
        renderer.setSize(size.width, size.height);
      },
      dispose: () => {
        disposed = true;
        cancelAnimationFrame(frameId);
        controls.dispose();
        if (mesh) {
          mesh.geometry.dispose();
          (mesh.material as THREE.Material).dispose();
          scene.remove(mesh);
        }
        if (edges) {
          edges.geometry.dispose();
          (edges.material as THREE.Material).dispose();
          scene.remove(edges);
        }
        if (gridHelper) {
          gridHelper.dispose();
          scene.remove(gridHelper);
        }
        if (axesHelper) scene.remove(axesHelper);
        renderer.dispose();
        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      },
    };

    apiRef.current = api;

    const handleResize = () => api.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      api.dispose();
      apiRef.current = null;
      setReady(false);
    };
  }, [arrayBuffer]);

  useEffect(() => {
    if (!ready) return;
    apiRef.current?.applySettings({
      displayMode,
      color: modelColor,
      opacity,
      smoothShading,
      showGrid,
      showAxes,
      autoRotate,
      orthographic,
      darkBackground,
    });
  }, [
    ready,
    displayMode,
    modelColor,
    opacity,
    smoothShading,
    showGrid,
    showAxes,
    autoRotate,
    orthographic,
    darkBackground,
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (document.fullscreenElement) {
          document.exitFullscreen();
          return;
        }
        onClose();
      }
      if (e.key.toLowerCase() === "r") apiRef.current?.fitToView();
      if (e.key.toLowerCase() === "g") setShowGrid((v) => !v);
      if (e.key.toLowerCase() === "a") setShowAxes((v) => !v);
      if (e.key.toLowerCase() === "w") {
        setDisplayMode((m) => (m === "wireframe" ? "solid" : "wireframe"));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      requestAnimationFrame(() => apiRef.current?.resize());
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen();
      else await document.exitFullscreen();
    } catch {
      // ignore fullscreen failures
    }
  }, []);

  const applyPreset = (preset: ViewPreset) => {
    setActivePreset(preset);
    apiRef.current?.setViewPreset(preset);
  };

  const displayError = error || renderError;
  const controlsDisabled = !ready || Boolean(displayError);

  return (
    <div ref={shellRef} className="flex h-screen w-screen flex-col overflow-hidden bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-800">
              {fileName}
            </h3>
            <p className="text-xs text-gray-500">
              Left-drag rotate · Right-drag / Shift-drag pan · Scroll zoom · Esc
              close tab
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ToolbarBtn
              title="Fullscreen"
              onClick={toggleFullscreen}
              disabled={controlsDisabled}
            >
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </ToolbarBtn>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title="Close tab"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <ToolbarBtn
            title="Solid"
            active={displayMode === "solid"}
            disabled={controlsDisabled}
            onClick={() => setDisplayMode("solid")}
          >
            Solid
          </ToolbarBtn>
          <ToolbarBtn
            title="Wireframe (W)"
            active={displayMode === "wireframe"}
            disabled={controlsDisabled}
            onClick={() => setDisplayMode("wireframe")}
          >
            Wireframe
          </ToolbarBtn>
          <ToolbarBtn
            title="Solid with edges"
            active={displayMode === "solid-edges"}
            disabled={controlsDisabled}
            onClick={() => setDisplayMode("solid-edges")}
          >
            Edges
          </ToolbarBtn>

          <span className="mx-1 h-5 w-px bg-gray-300" />

          <ToolbarBtn
            title="Grid (G)"
            active={showGrid}
            disabled={controlsDisabled}
            onClick={() => setShowGrid((v) => !v)}
          >
            <span className="inline-flex items-center gap-1">
              <FiGrid /> Grid
            </span>
          </ToolbarBtn>
          <ToolbarBtn
            title="Axes (A)"
            active={showAxes}
            disabled={controlsDisabled}
            onClick={() => setShowAxes((v) => !v)}
          >
            Axes
          </ToolbarBtn>
          <ToolbarBtn
            title="Auto-rotate"
            active={autoRotate}
            disabled={controlsDisabled}
            onClick={() => setAutoRotate((v) => !v)}
          >
            <span className="inline-flex items-center gap-1">
              <FiRotateCw /> Spin
            </span>
          </ToolbarBtn>
          <ToolbarBtn
            title="Orthographic camera"
            active={orthographic}
            disabled={controlsDisabled}
            onClick={() => setOrthographic((v) => !v)}
          >
            Ortho
          </ToolbarBtn>
          <ToolbarBtn
            title="Dark background"
            active={darkBackground}
            disabled={controlsDisabled}
            onClick={() => setDarkBackground((v) => !v)}
          >
            Dark
          </ToolbarBtn>
          <ToolbarBtn
            title="Smooth / flat shading"
            active={smoothShading}
            disabled={controlsDisabled}
            onClick={() => setSmoothShading((v) => !v)}
          >
            {smoothShading ? "Smooth" : "Flat"}
          </ToolbarBtn>

          <span className="mx-1 h-5 w-px bg-gray-300" />

          {PRESETS.map((preset) => (
            <ToolbarBtn
              key={preset}
              title={`${preset} view`}
              active={activePreset === preset}
              disabled={controlsDisabled}
              onClick={() => applyPreset(preset)}
            >
              {preset[0].toUpperCase() + preset.slice(1)}
            </ToolbarBtn>
          ))}

          <ToolbarBtn
            title="Fit to view (R)"
            disabled={controlsDisabled}
            onClick={() => apiRef.current?.fitToView()}
          >
            <span className="inline-flex items-center gap-1">
              <FiRefreshCw /> Fit
            </span>
          </ToolbarBtn>
          <ToolbarBtn
            title="Save screenshot"
            disabled={controlsDisabled}
            onClick={() => apiRef.current?.takeScreenshot(fileName)}
          >
            <span className="inline-flex items-center gap-1">
              <FiCamera /> Shot
            </span>
          </ToolbarBtn>

          <span className="mx-1 h-5 w-px bg-gray-300" />

          <label className="flex items-center gap-2 text-xs text-gray-600">
            Opacity
            <input
              type="range"
              min={0.15}
              max={1}
              step={0.05}
              value={opacity}
              disabled={controlsDisabled}
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="w-24"
            />
          </label>

          <div className="flex items-center gap-1">
            {MODEL_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                title={`Color ${c}`}
                disabled={controlsDisabled}
                onClick={() => setModelColor(c)}
                className={`h-5 w-5 rounded-full border ${
                  modelColor === c
                    ? "ring-2 ring-[#1DA8C6] ring-offset-1"
                    : "border-gray-300"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
            <input
              type="color"
              value={modelColor}
              disabled={controlsDisabled}
              onChange={(e) => setModelColor(e.target.value)}
              className="h-6 w-6 cursor-pointer rounded border border-gray-300 bg-transparent p-0 disabled:cursor-not-allowed"
              title="Custom color"
            />
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1">
          <div className="relative min-h-0 min-w-0 flex-1 bg-gray-100">
            {(loading || (!arrayBuffer && !displayError)) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-gray-500">
                Loading STL…
              </div>
            )}
            {displayError && (
              <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-red-500">
                {displayError}
              </div>
            )}
            <div ref={containerRef} className="h-full w-full" />
          </div>

          {stats && (
            <aside className="hidden w-52 shrink-0 border-l border-gray-200 bg-white p-3 text-xs text-gray-700 md:block">
              <h4 className="mb-2 font-semibold text-gray-800">Model info</h4>
              <dl className="space-y-2">
                <div>
                  <dt className="text-gray-500">Triangles</dt>
                  <dd className="font-medium">
                    {stats.triangles.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Vertices</dt>
                  <dd className="font-medium">
                    {stats.vertices.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Size X</dt>
                  <dd className="font-medium">{formatMm(stats.sizeX)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Size Y</dt>
                  <dd className="font-medium">{formatMm(stats.sizeY)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Size Z</dt>
                  <dd className="font-medium">{formatMm(stats.sizeZ)}</dd>
                </div>
              </dl>
              <p className="mt-4 text-[11px] leading-relaxed text-gray-400">
                Shortcuts: W wireframe · G grid · A axes · R fit · Esc close
              </p>
            </aside>
          )}
        </div>
    </div>
  );
};

export default StlViewer;
